import type { InputHTMLAttributes } from 'react'
import { cn } from '../lib/cn'
import { inputClassName, useFieldControl } from './field'

/**
 * Valor em reais. O dado é sempre centavos inteiros; a máscara é só da tela: digita-se da direita
 * para a esquerda ("19000" vira 190,00) e o prefixo R$ fica fixo no campo. Teclado numérico no
 * celular. Vazio é `null`, não zero.
 */
export interface MoneyInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'inputMode'
> {
  /** Centavos inteiros, ou `null` quando vazio. */
  value: number | null
  onChange: (cents: number | null) => void
  /** Teto em centavos; o que passa disso é ignorado. Padrão: R$ 9.999.999,99. */
  max?: number
}

const formatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export function MoneyInput({
  value,
  onChange,
  max = 999_999_999,
  className,
  placeholder = '0,00',
  ...props
}: MoneyInputProps) {
  const field = useFieldControl()
  return (
    <div data-slot="money-input" className="relative">
      <span
        aria-hidden
        className="font-body text-label pointer-events-none absolute top-1/2 left-[14px] -translate-y-1/2 text-[13px] leading-none font-light"
      >
        R$
      </span>
      <input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={value === null ? '' : formatter.format(value / 100)}
        placeholder={placeholder}
        onChange={(event) => {
          const digits = event.target.value.replace(/\D/g, '')
          if (digits === '') return onChange(null)
          const cents = Number(digits)
          if (cents <= max) onChange(cents)
        }}
        className={cn(inputClassName, 'pl-10 tabular-nums', className)}
        {...field}
        {...props}
      />
    </div>
  )
}
